import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  AppBar,
  Autocomplete,
  Avatar,
  Box,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { decode } from "base-64";
import React, { useEffect, useState } from "react";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Outlet, useNavigate } from "react-router-dom";
import { api } from "../api";
import { fetchName, fetchToken } from "../auth/Token";
import { ColorModeContext, useMode } from "../theme";
import Sidebarr from "./Sidebar";
import InputAdornment from "@mui/material/InputAdornment";
import { styled, lighten, darken } from "@mui/system";
import { toast } from 'sonner';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === "light"
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [theme, colorMode] = useMode();
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [userPicRaw, setUserPicRaw] = useState([]);
  const [personaInfo, setPersonaInfo] = useState([]);
  const [detailsInfo, setDetailsInfo] = useState([]);
  const [level_data, setLevel_data] = useState([]);
  const [loading, setLoading] = useState(false);
  const [to_go, setTo_go] = useState("")

  const go = () => {
    navigate(to_go);
  }

  const fetchother = () => {
    setLoading(true);
  
    // Fetch user details
    const storedUserPicRaw = localStorage.getItem('userPicRaw');
    if (storedUserPicRaw) {
      setUserPicRaw(JSON.parse(storedUserPicRaw));
    } else {
      api
        .get("/users/details", {
          headers: {
            Authorization: "Bearer " + fetchToken(),
          },
        })
        .then(function (response) {
          setUserPicRaw(response.data);
          localStorage.setItem('userPicRaw', JSON.stringify(response.data)); 
        })
        .catch(function (error) {
          console.log(error);
        });
    }
  
    // Fetch dashboard details for all levels
    const storedLevelData = localStorage.getItem('level_data');
    if (storedLevelData) {
      setLevel_data(JSON.parse(storedLevelData));
    } else {
      api
        .get("/dashboards/details/all/levels", {
          headers: {
            Authorization: "Bearer " + fetchToken(),
          },
        })
        .then(function (response) {
          const groupedData = [
            ...response.data?.societies.map((item) => ({
              label: item.society,
              id: item.id,
              group: "Societies",
            })),
            ...response.data?.communities.map((item) => ({
              label: item.community_name,
              id: item.community_id,
              group: "Communities",
            })),
            ...response.data?.clusters.map((item) => ({
              label: item.cluster_name,
              id: item.cluster_id,
              group: "Clusters",
            })),
            ...response.data?.associations.map((item) => ({
              label: item.association_name,
              id: item.association_id,
              group: "Savings Groups",
            })),
          ];
          setLevel_data(groupedData);
          localStorage.setItem('level_data', JSON.stringify(groupedData)); 
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(function () {
          setLoading(false);
        });
    }
  
    // Fetch user details (all)
    const storedDetailsInfo = localStorage.getItem('detailsInfo');
    if (storedDetailsInfo) {
      setDetailsInfo(JSON.parse(storedDetailsInfo));
    } else {
      api
        .get("/users/details/all", {
          headers: {
            Authorization: "Bearer " + fetchToken(),
          },
        })
        .then(function (response) {
          setDetailsInfo(response.data?.details);
          localStorage.setItem('detailsInfo', JSON.stringify(response.data?.details)); 
        })
        .catch(function (error) {
          console.log(error);
        });
    }
  
    // Fetch persona details
    const storedPersonaInfo = localStorage.getItem('personaInfo');
    if (storedPersonaInfo) {
      setPersonaInfo(JSON.parse(storedPersonaInfo));
    } else {
      api
        .get("/users/su", {
          headers: {
            Authorization: "Bearer " + fetchToken(),
          },
        })
        .then(function (response) {
          setPersonaInfo(response.data);
          setTo_go(
            response.data?.role_name === "Credit Officer"?
              "/credit-officer": 
            response.data?.role_name === "Operations Manager"?
              "/operations-manager" :
            response.data?.role_name === "Society Manager"?
              "/society-manager" :
            response.data?.role_name === "Cashier"?
              "/cashier" :
              "/dashboard"
          );
          localStorage.setItem('personaInfo', JSON.stringify(response.data));
        })
        .catch(function (error) {
          console.log(error);
        });
    }
  };


  useEffect(() => {
    fetchother();
  }, []);

  const handleGroupChange = (event, value) => {
    if (value) {
      switch (value.group) {
        case "Societies":
          toast.promise(
            new Promise((resolve) => {
              navigate(`/allassociationKind/${value.id}`);
              resolve();
            }),
            {
              loading: 'Navigating...',
              success: 'Successfully navigated',
              error: 'Error navigating to Societies',
            }
          );
          break;
        case "Communities":
          toast.promise(
            api.get(`/association/get/one/community/data/${value.id}`, {
              headers: {
                Authorization: "Bearer " + fetchToken(),
              },
            }),
            {
              loading: 'Loading community data...',
              success: (response) => {
                navigate("/community", {
                  state: {
                    community_id: response.data?.community?.community_id,
                    cluster_name: response.data?.community?.cluster_name,
                    society_id: response.data?.community?.id,
                    society_name: response.data?.community?.society_name,
                    community_name: response.data?.community?.community_name,
                  },
                });
                return 'Successfully navigated';
              },
              error: 'Error loading community data',
            }
          );
          break;
        case "Clusters":
          toast.promise(
            api.get(`/association/get/one/cluster/data/${value.id}`, {
              headers: {
                Authorization: "Bearer " + fetchToken(),
              },
            }),
            {
              loading: 'Loading cluster data...',
              success: (response) => {
                navigate('/cluster', {
                  state: {
                    cluster_id: response.data?.cluster?.cluster_id,
                    society_name: response.data?.cluster?.society,
                    society_id: response.data?.cluster?.id,
                    cluster_name: response.data?.cluster?.cluster_name,
                    cluster_office: response.data?.cluster?.cluster_office,
                  },
                });
                return 'Successfully navigated';
              },
              error: 'Error loading cluster data',
            }
          );
          break;
  
        case "Savings Groups":
          toast.promise(
            api.get(`association/${value.id}`, {
              headers: {
                Authorization: 'Bearer ' + fetchToken(),
              },
            }),
            {
              loading: 'Loading Group data...',
              success: (response) => {
                if (response.status === 200 && response.data) {
                  navigate('/association/', { state: { responseData: response.data } });
                  return 'Successfully navigated';
                }
                return 'No data found';
              },
              error: 'Error loading Group data',
            }
          );
      }
    }
  };

  const pic = (pic) => {
    const de = decode(pic);
    return `data:image/jpeg;base64,${btoa(de)}`;
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const signOut = () => {
    localStorage.clear();
    navigate("/");
  };

  const reload = () => {
    for (let key in localStorage) {
      if (key !== "afrifaToken" && key !== "authData") {
        localStorage.removeItem(key);
      }
    }
    window.location.reload();
  };
  

  const handleAccountPage = () => {
    handleCloseUserMenu();
    navigate("/my_account");
  };

  const handleGoToProfile = () => {
    handleCloseUserMenu();
    navigate("/profile/page", {
      state: {
        personaInfo: personaInfo,
        detailsInfo: detailsInfo,
      },
    });
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* AppBar */}
          <Box>
            <AppBar
              position="fixed"
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: "#10002b",
              }}
            >
              <Toolbar>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2 }}
                  >
                    <MenuRoundedIcon />
                  </IconButton>
                  <IconButton
                    variant="soft"
                    color="primary"
                    size="sm"
                    // onClick={() => console.log(to_go)}
                    onClick={go}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/graph.png`}
                      alt={fetchName()}
                      style={{ width: "20px", height: "20px" }}
                    />

                    <Typography
                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                      sx={{ color: "white", ml: 1 }}
                      variant="h5"
                      noWrap
                    >
                      <b>The Cooperative</b>
                    </Typography>
                  </IconButton>
                </Box>

                <Box sx={{ ml: "auto" }}>
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    {
                      personaInfo.role_name === "Supervisors"?
                      null:
                      <>
                    <Tooltip title="Refresh Page">
                      <IconButton onClick={reload}>
                        <CachedRoundedIcon sx={{ color:'white' }}/>
                      </IconButton>
                    </Tooltip>
                      <Autocomplete
                      id="grouped-autocomplete"
                      options={level_data.sort(
                        (a, b) => -b.group.localeCompare(a.group)
                      )}
                      groupBy={(option) => option.group}
                      getOptionLabel={(option) => option.label}
                      loading={loading}
                      onChange={handleGroupChange}
                      sx={{
                        width: 500,
                        borderRadius: 5,
                        m: 1,
                        backgroundColor: "whitesmoke",
                      }}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchRoundedIcon />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            color: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 5,
                            },
                            "& .MuiInputBase-root": {
                              borderRadius: 5,
                            },
                          }}
                        />
                      )}
                      renderGroup={(params) => (
                        <li key={params.key}>
                          <GroupHeader>
                          <Typography
                              sx={{ mr: 1, textAlign:'center' }}
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                            >
                               <b>{params.group}</b>
                            </Typography>
                          </GroupHeader>
                          <GroupItems>
                            <Typography
                              sx={{ mr: 1 }}
                              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                            >
                              <b>{params.children}</b>
                            </Typography>
                          </GroupItems>
                        </li>
                      )}
                    />
                    </>
                    }
                    

                    <Typography
                      sx={{ mr: 2, my: "auto", color: "white" }}
                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                      variant="h6"
                    >
                      <b>{personaInfo.role_name}</b>
                    </Typography>
                    <Tooltip title="Open Menu">
                      <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar
                          alt="Me"
                          src={
                            userPicRaw
                              ? pic(userPicRaw)
                              : "data:image/png;base64,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"
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem key={1} onClick={handleGoToProfile}>
                      <Typography
                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                        textAlign="center"
                        fontWeight="bold"
                      >
                        Profile
                      </Typography>
                    </MenuItem>
                    <MenuItem key={2} onClick={handleAccountPage}>
                      <Typography
                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                        textAlign="center"
                        fontWeight="bold"
                      >
                        My account
                      </Typography>
                    </MenuItem>
                    <MenuItem key={3} onClick={signOut}>
                      <Typography
                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                        textAlign="center"
                        sx={{ color: "red" }}
                        fontWeight="bold"
                      >
                        Sign Out
                      </Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Toolbar>
            </AppBar>
          </Box>

          {/* Main content area */}
          <Box
            component="main"
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              height: "100dvh",
              pt: 10,
              // px: { xs: 2, md: 6 },
              // pb: { xs: 2, sm: 2, md: 3 },
            }}
          >
            <Box
              sx={{
                width: isSidebarOpen ? 40 : 220,
                transition: "width 0.3s",
                flexShrink: 0,
              }}
            >
              <Sidebarr
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "7px",
                  height: "7px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
                p: 1,
                m: 2,
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
